import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { hinweis } from '../components/Hinweise';

const TYP_LABEL = {
  text: 'Text', textarea: 'Textbox', select: 'Auswahlliste', boolean: 'Ja/Nein (Häkchen)', date: 'Datum',
  number: 'Zahl', email: 'E-Mail', tel: 'Telefon', radio: 'Auswahlknöpfe', ueberschrift: 'Zwischenüberschrift',
};
const ZUSATZ_TYPEN = ['text', 'textarea', 'select', 'boolean', 'date', 'number', 'email'];
const MIT_PLATZHALTER = ['text', 'textarea', 'number', 'email', 'tel'];

const kleinLabel = { fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2, display: 'block' };
const badge = (bg, color) => ({ padding: '1px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700, background: bg, color, whiteSpace: 'nowrap' });

function FeldZeile({ f, idx, anzahl, onUpdate, onMove, onRemove }) {
  const istUeberschrift = f.typ === 'ueberschrift';
  const set = (k, v) => onUpdate(f.id, { [k]: v });

  return (
    <div style={{
      marginBottom: '0.6rem', padding: '0.7rem 0.75rem', borderRadius: 6,
      background: istUeberschrift ? '#eef2f7' : f.system ? '#f8f9fa' : '#fffdf5',
      border: `1px solid ${f.system ? 'var(--border)' : '#f0e2b6'}`,
      opacity: f.ausgeblendet ? 0.6 : 1,
    }}>
      {/* Kopfzeile */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', minWidth: 22 }}>{idx + 1}.</span>
        {istUeberschrift
          ? <span style={badge('#dde6f0', '#003366')}>Zwischenüberschrift</span>
          : f.system
            ? <span style={badge('#e3f2fd', '#1565c0')} title="Wird in einer festen Datenbankspalte gespeichert">🔒 Systemfeld · {TYP_LABEL[f.typ] || f.typ}</span>
            : <span style={badge('#fff3cd', '#8a6d00')}>Zusatzfeld</span>}
        {f.ausgeblendet && <span style={badge('#eceff1', '#546e7a')}>ausgeblendet</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 9px' }}
            onClick={() => onMove(idx, -1)} disabled={idx === 0} title="Nach oben">↑</button>
          <button type="button" className="btn btn-secondary btn-sm" style={{ padding: '2px 9px' }}
            onClick={() => onMove(idx, 1)} disabled={idx === anzahl - 1} title="Nach unten">↓</button>
          {!f.system && (
            <button type="button" className="btn btn-danger btn-sm" style={{ padding: '2px 9px' }}
              onClick={() => onRemove(f)} title="Feld entfernen">✕</button>
          )}
        </div>
      </div>

      {/* Hauptzeile */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 220px' }}>
          <label style={kleinLabel}>{istUeberschrift ? 'Überschrift' : 'Beschriftung / Frage'}</label>
          <input className="form-control" value={f.label} maxLength={200}
            placeholder={istUeberschrift ? 'z.B. Weitere Angaben' : 'z.B. T-Shirt-Größe'}
            onChange={e => set('label', e.target.value)} />
        </div>

        {!f.system && !istUeberschrift && (
          <div style={{ flex: '0 0 150px' }}>
            <label style={kleinLabel}>Typ</label>
            <select className="form-control" value={f.typ} onChange={e => set('typ', e.target.value)}>
              {ZUSATZ_TYPEN.map(t => <option key={t} value={t}>{TYP_LABEL[t]}</option>)}
            </select>
          </div>
        )}

        {!istUeberschrift && (
          <div style={{ flex: '0 0 120px' }}>
            <label style={kleinLabel}>Breite</label>
            <select className="form-control" value={f.breite || 'voll'} onChange={e => set('breite', e.target.value)}>
              <option value="voll">Ganze Zeile</option>
              <option value="halb">Halbe Zeile</option>
            </select>
          </div>
        )}

        {!istUeberschrift && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.85rem', height: 36, cursor: f.pflicht_fest ? 'not-allowed' : 'pointer' }}
            title={f.pflicht_fest ? 'Dieses Feld wird für die Verarbeitung zwingend benötigt' : ''}>
            <input type="checkbox" checked={!!f.pflicht} disabled={f.pflicht_fest}
              onChange={e => set('pflicht', e.target.checked)} style={{ width: 16, height: 16 }} />
            Pflicht{f.pflicht_fest && ' 🔒'}
          </label>
        )}

        {f.system && !f.pflicht_fest && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.85rem', height: 36, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!f.ausgeblendet}
              onChange={e => set('ausgeblendet', e.target.checked)} style={{ width: 16, height: 16 }} />
            Ausblenden
          </label>
        )}
      </div>

      {/* Hinweis / Platzhalter */}
      {!istUeberschrift && (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={kleinLabel}>Hinweis neben der Beschriftung (optional)</label>
            <input className="form-control" value={f.hinweis || ''} maxLength={300}
              placeholder="z.B. Was kann das Kind schon?"
              onChange={e => set('hinweis', e.target.value)} />
          </div>
          {MIT_PLATZHALTER.includes(f.typ) && (
            <div style={{ flex: '1 1 220px' }}>
              <label style={kleinLabel}>Platzhalter im leeren Feld (optional)</label>
              <input className="form-control" value={f.platzhalter || ''} maxLength={200}
                onChange={e => set('platzhalter', e.target.value)} />
            </div>
          )}
        </div>
      )}

      {/* Optionen */}
      {!f.system && f.typ === 'select' && (
        <div style={{ marginTop: '0.5rem' }}>
          <label style={kleinLabel}>Optionen (kommagetrennt)</label>
          <input className="form-control"
            value={f.optionenText ?? (f.optionen || []).join(', ')}
            placeholder="Option A, Option B, Option C"
            onChange={e => onUpdate(f.id, {
              optionenText: e.target.value,
              optionen: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
            })} />
        </div>
      )}

      {f.system && Array.isArray(f.optionen) && f.optionen.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <label style={kleinLabel}>Beschriftung der Auswahlmöglichkeiten (die Auswahlwerte selbst sind fest)</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.35rem' }}>
            {f.optionen.map(o => (
              <div key={o.wert} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <code style={{ fontSize: '0.7rem', color: 'var(--text-muted)', minWidth: 70 }}>{o.wert}</code>
                <input className="form-control" style={{ padding: '4px 8px' }} value={o.label} maxLength={100}
                  onChange={e => set('optionen', f.optionen.map(x => x.wert === o.wert ? { ...x, label: e.target.value } : x))} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function KachelForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'neu';

  const [kachel, setKachel] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Allgemein
  const [titel, setTitel] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [aktiv, setAktiv] = useState(false);
  const [bestaetigung, setBestaetigung] = useState('');
  const [key, setKey] = useState('');
  const [keyError, setKeyError] = useState('');

  // Formular-Felder (alle, in Anzeigereihenfolge)
  const [felder, setFelder] = useState([]);

  // Einreichungen (nur custom)
  const [einreichungen, setEinreichungen] = useState([]);
  const [showEinreichungen, setShowEinreichungen] = useState(false);
  const [loadingEinreichungen, setLoadingEinreichungen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get('/api/admin/kacheln').then(r => {
      const k = r.data.find(x => String(x.id) === String(id));
      if (!k) { navigate('/kacheln'); return; }
      setKachel(k);
      setTitel(k.titel);
      setBeschreibung(k.beschreibung || '');
      setAktiv(k.aktiv);
      setBestaetigung(k.bestaetigung_text || '');
      setFelder(k.formular_felder || []);
    }).catch(() => navigate('/kacheln'))
    .finally(() => setLoading(false));
  }, [id, isNew]);

  const handleKeyChange = (val) => {
    const slug = val.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    setKey(slug);
    setKeyError('');
  };

  const neueId = () => `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

  const addFeld = () => setFelder(p => [...p, {
    id: neueId(), label: '', typ: 'text', pflicht: false, optionen: [], hinweis: '', platzhalter: '', breite: 'voll',
  }]);

  const addUeberschrift = () => setFelder(p => [...p, { id: neueId(), label: '', typ: 'ueberschrift', pflicht: false }]);

  const removeFeld = (f) => {
    if (f.label.trim() && !window.confirm(`„${f.label}" entfernen?\n\nBereits eingegangene Antworten bleiben in den Einträgen erhalten.`)) return;
    setFelder(p => p.filter(x => x.id !== f.id));
  };

  const updateFeld = (fid, aenderung) => setFelder(p => p.map(f => f.id === fid ? { ...f, ...aenderung } : f));

  const moveFeld = (idx, dir) => {
    const next = [...felder];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setFelder(next);
  };

  const felderPruefen = () => {
    const gesehen = new Set();
    for (const f of felder) {
      if (f.system || f.typ === 'ueberschrift' || !f.label.trim()) continue;
      const k = f.label.trim().toLowerCase();
      if (gesehen.has(k)) return `Die Beschriftung „${f.label.trim()}" kommt mehrfach vor. Zusatzfelder brauchen eindeutige Beschriftungen.`;
      gesehen.add(k);
      if (f.typ === 'select' && !(f.optionen || []).length) return `Das Auswahlfeld „${f.label.trim()}" hat noch keine Optionen.`;
    }
    return null;
  };

  const handleSave = async () => {
    if (!titel.trim()) return setError('Titel ist Pflicht');
    if (isNew && !key.trim()) return setKeyError('Key ist Pflicht');
    const problem = isNew ? null : felderPruefen();
    if (problem) return setError(problem);

    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const r = await api.post('/api/admin/kacheln', {
          key, titel: titel.trim(), beschreibung: beschreibung.trim() || null,
        });
        // Direkt weiter zur Bearbeitung, damit Felder angelegt werden können
        navigate(`/kacheln/${r.data.id}/bearbeiten`, { replace: true });
      } else {
        await api.put(`/api/admin/kacheln/${id}`, {
          titel: titel.trim(),
          beschreibung: beschreibung.trim() || null,
          aktiv,
          // Leere Zusatzfelder weglassen; Systemfelder ohne Beschriftung erhalten serverseitig den Standard
          formular_felder: felder
            .filter(f => f.system || f.label.trim())
            .map(({ optionenText, ...f }) => f),
          bestaetigung_text: bestaetigung.trim() || null,
        });
        navigate('/kacheln');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const loadEinreichungen = async () => {
    setLoadingEinreichungen(true);
    try {
      const r = await api.get(`/api/admin/kacheln/${id}/einreichungen`);
      setEinreichungen(r.data);
      setShowEinreichungen(true);
    } catch { hinweis.fehler('Fehler beim Laden'); }
    finally { setLoadingEinreichungen(false); }
  };

  const typ = isNew ? 'formular_custom' : (kachel?.typ || 'formular');

  if (loading) return <div style={{ textAlign: 'center', padding: '3rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <div className="page-header">
        <h2 className="page-title">{isNew ? 'Neue Kachel erstellen' : `Kachel: ${kachel?.titel}`}</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {!isNew && typ === 'formular_custom' && (
            <button className="btn btn-secondary btn-sm" onClick={loadEinreichungen} disabled={loadingEinreichungen}>
              {loadingEinreichungen ? 'Lädt…' : `📥 Einreichungen`}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/kacheln')}>Abbrechen</button>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* ─── Allgemein ─── */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <p style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Allgemein
        </p>

        {isNew && (
          <div className="form-group">
            <label className="form-label">
              Key (URL-Kürzel) <span style={{ color: 'red' }}>*</span>
              <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Nur Kleinbuchstaben und Bindestriche, z.B. <code>mitgliederversammlung</code>
              </span>
            </label>
            <input className="form-control" value={key} maxLength={50}
              onChange={e => handleKeyChange(e.target.value)}
              placeholder="z.B. mitgliederversammlung"
              style={keyError ? { borderColor: 'var(--danger)' } : {}} />
            {key && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
              Route im Widget: <code>/f/{key}</code>
            </div>}
            {keyError && <div style={{ color: 'var(--danger)', fontSize: '0.82rem', marginTop: 4 }}>{keyError}</div>}
          </div>
        )}

        {!isNew && typ === 'formular_custom' && (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '0.75rem', background: '#f4f7fb', padding: '6px 10px', borderRadius: 6 }}>
            Key: <code>{kachel?.key}</code> · Route im Widget: <code>{kachel?.route}</code>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">Titel <span style={{ color: 'red' }}>*</span></label>
          <input className="form-control" value={titel} maxLength={100}
            onChange={e => setTitel(e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">Beschreibung <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
          <textarea className="form-control" rows={2} value={beschreibung}
            onChange={e => setBeschreibung(e.target.value)}
            placeholder="Kurzer Text, der auf der Kachel angezeigt wird" />
        </div>

        {!isNew && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={aktiv} onChange={e => setAktiv(e.target.checked)} style={{ width: 16, height: 16 }} />
            Kachel aktiv (im Widget sichtbar)
          </label>
        )}

        {typ !== 'angebote' && (
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <label className="form-label">Erfolgsmeldung nach dem Absenden <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
            <textarea className="form-control" rows={3} value={bestaetigung}
              onChange={e => setBestaetigung(e.target.value)}
              placeholder="z.B. Vielen Dank für deine Anmeldung! Du erhältst in Kürze eine Bestätigung per E-Mail." />
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4 }}>
              Leer lassen für den Standard-Text
            </div>
          </div>
        )}
      </div>

      {/* ─── Formular-Baukasten ─── */}
      {typ === 'angebote' ? (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Die Angebote-Kachel leitet auf die Angebotsliste weiter. Das Formular wird direkt beim jeweiligen Angebot konfiguriert.
          </p>
        </div>
      ) : isNew ? (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>
            Nach dem Erstellen öffnet sich die Bearbeitung – dort legst du die Formular-Felder an.
            Name und E-Mail werden immer abgefragt.
          </p>
        </div>
      ) : (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <p style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Formular-Felder ({felder.filter(f => f.typ !== 'ueberschrift').length})
          </p>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: 1.6 }}>
            Die Felder erscheinen im Formular in genau dieser Reihenfolge.
            <strong> 🔒 Systemfelder</strong> werden in festen Spalten gespeichert und weiterverarbeitet
            (z.B. E-Mail-Versand, Wartelisten-Reihenfolge, Aufnahme in eine Gruppe) – sie lassen sich umbenennen
            und verschieben, aber nicht löschen; optionale Systemfelder kann man ausblenden.
            Zwei aufeinanderfolgende Felder mit <em>halber Zeile</em> stehen nebeneinander (auf dem Handy untereinander).
          </div>

          {felder.map((f, idx) => (
            <FeldZeile key={f.id} f={f} idx={idx} anzahl={felder.length}
              onUpdate={updateFeld} onMove={moveFeld} onRemove={removeFeld} />
          ))}

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addFeld}>+ Feld hinzufügen</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addUeberschrift}>+ Zwischenüberschrift</button>
          </div>
        </div>
      )}

      {/* ─── Einreichungen ─── */}
      {showEinreichungen && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <p style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', margin: 0 }}>
              Einreichungen ({einreichungen.length})
            </p>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowEinreichungen(false)}>Ausblenden</button>
          </div>
          {einreichungen.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Noch keine Einreichungen.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: 400, overflowY: 'auto' }}>
              {einreichungen.map(e => (
                <div key={e.id} style={{ background: '#f8f9fa', borderRadius: 6, padding: '0.6rem 0.75rem', border: '1px solid var(--border)', fontSize: '0.82rem' }}>
                  <div style={{ color: 'var(--text-muted)', marginBottom: 4 }}>
                    {new Date(e.created_at).toLocaleString('de-DE')}
                  </div>
                  {Object.entries(e.felder || {}).map(([k, v]) => (
                    <div key={k}><strong>{k}:</strong> {String(v)}</div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button className="btn btn-secondary" onClick={() => navigate('/kacheln')}>Abbrechen</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Speichern…' : isNew ? 'Erstellen' : '✓ Speichern'}
        </button>
      </div>
    </div>
  );
}
